import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Row, Col, Typography, Card, Table, Input, Alert, Spin } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { format, subDays } from "date-fns";
import withClinic from "../../hooks/with_clinic";
import DateRange from "../../components/date_range";
import { Clinic } from "../../store";
import {
  BookingReportType,
  get_booking_reports,
  getBookingReportsVar,
  GetBookingReportsData,
  ServiceBookingRow,
  PractitionerServiceRow,
} from "../../graphql/booking_reports";

interface P {
  currentClinic?: Clinic;
}

function formatMonthDisplay(monthStr: string): string {
  // monthStr: "YYYY-MM"
  const [y, m] = monthStr.split("-");
  const year = Number(y);
  const month = Number(m);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthStr;
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

type ServiceSummary = {
  serviceName: string;
  totalBookings: number;
  change: number; // percentage vs previous month
};

function calculateServiceSummary(rows: ServiceBookingRow[]): ServiceSummary[] {
  const serviceMap: Record<
    string,
    { totalBookings: number; byMonth: Record<string, number> }
  > = {};

  for (const r of rows) {
    const serviceName = (r.serviceName || "").trim();
    if (!serviceName) continue;
    if (!serviceMap[serviceName]) {
      serviceMap[serviceName] = { totalBookings: 0, byMonth: {} };
    }
    serviceMap[serviceName].totalBookings += r.bookingCount || 0;
    serviceMap[serviceName].byMonth[r.month] =
      (serviceMap[serviceName].byMonth[r.month] || 0) + (r.bookingCount || 0);
  }

  const summaryArray: ServiceSummary[] = Object.entries(serviceMap).map(
    ([serviceName, data]) => {
      const months = Object.keys(data.byMonth).sort((a, b) => b.localeCompare(a));
      let change = 0;
      if (months.length >= 2) {
        const currentMonth = months[0];
        const previousMonth = months[1];
        const currentBookings = data.byMonth[currentMonth] || 0;
        const previousBookings = data.byMonth[previousMonth] || 0;
        if (previousBookings > 0) {
          change = ((currentBookings - previousBookings) / previousBookings) * 100;
        }
      }
      return {
        serviceName,
        totalBookings: data.totalBookings,
        change,
      };
    }
  );

  return summaryArray.sort((a, b) => b.totalBookings - a.totalBookings);
}

const ServiceBehaviorReport = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";

  // Filters (match resolver args)
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Pagination state for each dataset
  const [monthPage, setMonthPage] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 20,
  });
  const [servicePage, setServicePage] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 20,
  });
  const [practitionerPage, setPractitionerPage] = useState<{
    current: number;
    pageSize: number;
  }>({
    current: 1,
    pageSize: 20,
  });

  const serviceVars = useMemo(
    () =>
      getBookingReportsVar({
        queryType: BookingReportType.SERVICE_BOOKINGS,
        clinicCode,
        fromDate: format(ranges.startDate, "yyyy-MM-dd"),
        toDate: format(ranges.endDate, "yyyy-MM-dd"),
        take: servicePage.pageSize,
        skip: (servicePage.current - 1) * servicePage.pageSize,
      }),
    [clinicCode, ranges.startDate, ranges.endDate, servicePage.current, servicePage.pageSize]
  );

  const monthlyVars = useMemo(
    () =>
      getBookingReportsVar({
        queryType: BookingReportType.MONTHLY_TOTALS,
        clinicCode,
        fromDate: format(ranges.startDate, "yyyy-MM-dd"),
        toDate: format(ranges.endDate, "yyyy-MM-dd"),
        take: monthPage.pageSize,
        skip: (monthPage.current - 1) * monthPage.pageSize,
      }),
    [clinicCode, ranges.startDate, ranges.endDate, monthPage.current, monthPage.pageSize]
  );

  const practitionerVars = useMemo(
    () =>
      getBookingReportsVar({
        queryType: BookingReportType.PRACTITIONER_SERVICES,
        clinicCode,
        fromDate: format(ranges.startDate, "yyyy-MM-dd"),
        toDate: format(ranges.endDate, "yyyy-MM-dd"),
        take: practitionerPage.pageSize,
        skip: (practitionerPage.current - 1) * practitionerPage.pageSize,
      }),
    [clinicCode, ranges.startDate, ranges.endDate, practitionerPage.current, practitionerPage.pageSize]
  );

  const serviceQ = useQuery<GetBookingReportsData>(get_booking_reports, {
    variables: serviceVars,
    skip: !clinicCode,
    fetchPolicy: "network-only",
  });

  const monthlyQ = useQuery<GetBookingReportsData>(get_booking_reports, {
    variables: monthlyVars,
    skip: !clinicCode,
    fetchPolicy: "network-only",
  });

  const practitionerQ = useQuery<GetBookingReportsData>(get_booking_reports, {
    variables: practitionerVars,
    skip: !clinicCode,
    fetchPolicy: "network-only",
  });

  const loading = serviceQ.loading || monthlyQ.loading || practitionerQ.loading;
  const error = serviceQ.error || monthlyQ.error || practitionerQ.error;

  const serviceBookings =
    serviceQ.data?.getBookingReports?.serviceBookings ?? [];
  const monthlyTotals = monthlyQ.data?.getBookingReports?.monthlyTotals ?? [];
  const practitionerServices: PractitionerServiceRow[] =
    practitionerQ.data?.getBookingReports?.practitionerServices ?? [];

  const serviceSummary = useMemo(
    () => calculateServiceSummary(serviceBookings),
    [serviceBookings]
  );

  const filteredServices = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return serviceSummary;
    return serviceSummary.filter((s) =>
      s.serviceName.toLowerCase().includes(search)
    );
  }, [serviceSummary, searchTerm]);

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const monthColumns: ColumnsType<{
    key: string;
    month: string;
    totalBookings: number;
  }> = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (value: string) => formatMonthDisplay(value),
    },
    {
      title: "Bookings",
      dataIndex: "totalBookings",
      key: "totalBookings",
      align: "right",
      render: (v: number) => v.toLocaleString(),
    },
  ];

  const serviceColumns: ColumnsType<ServiceSummary & { key: string; rank: number }> = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      align: "center",
    },
    {
      title: "Service Name",
      dataIndex: "serviceName",
      key: "serviceName",
    },
    {
      title: "Bookings",
      dataIndex: "totalBookings",
      key: "totalBookings",
      align: "right",
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "Change",
      dataIndex: "change",
      key: "change",
      align: "center",
      render: (change: number) => {
        const positive = change > 0;
        const negative = change < 0;
        const color = positive ? "#10b981" : negative ? "#ef4444" : "#64748b";
        const arrow = positive ? "▲" : negative ? "▼" : "•";
        return (
          <span style={{ color, fontWeight: 600 }}>
            <span style={{ marginRight: 4 }}>{arrow}</span>
            {Math.abs(change).toFixed(1)}%
          </span>
        );
      },
    },
  ];

  const practitionerColumns: ColumnsType<
    PractitionerServiceRow & { key: string }
  > = [
    {
      title: "Practitioner",
      dataIndex: "practitionerName",
      key: "practitionerName",
    },
    {
      title: "Service",
      dataIndex: "serviceName",
      key: "serviceName",
    },
    {
      title: "Bookings",
      dataIndex: "bookingCount",
      key: "bookingCount",
      align: "right",
      render: (v: number) => v.toLocaleString(),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Service Behavior Report
          </Typography.Title>
        </Col>
        <Col>
          <DateRange
            startDate={ranges.startDate}
            endDate={ranges.endDate}
            onSelect={onDateChange}
          />
        </Col>
      </Row>

      {!clinicCode && (
        <Alert
          type="warning"
          message="No clinic selected."
          style={{ marginBottom: 16 }}
        />
      )}

      {error && (
        <Alert
          type="error"
          message="Failed to load"
          description={error.message}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Monthly Service Bookings">
              <Table
                size="small"
                bordered
                rowKey={(r: { month: string }) => r.month}
                dataSource={monthlyTotals
                  .slice()
                  .sort((a, b) => b.month.localeCompare(a.month))
                  .map((m) => ({ ...m, key: m.month }))}
                columns={monthColumns}
                pagination={{
                  current: monthPage.current,
                  pageSize: monthPage.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (current: number, pageSize: number) =>
                    setMonthPage({ current, pageSize }),
                }}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title="Top Service Rankings"
              extra={
                <Input.Search
                  placeholder="Search services…"
                  allowClear
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  style={{ width: 260 }}
                />
              }
            >
              <Table
                size="small"
                bordered
                rowKey="key"
                dataSource={filteredServices.map((s, index) => ({
                  ...s,
                  key: s.serviceName,
                  rank: index + 1,
                }))}
                columns={serviceColumns}
                pagination={{
                  current: servicePage.current,
                  pageSize: servicePage.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (current: number, pageSize: number) =>
                    setServicePage({ current, pageSize }),
                }}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card title="Top Practitioner-Service Combinations">
              <Table
                size="small"
                bordered
                rowKey="key"
                dataSource={(practitionerServices || []).slice(0, 30).map((p, idx) => ({
                  ...p,
                  key: `${p.practitionerName}-${p.serviceName}-${idx}`,
                }))}
                columns={practitionerColumns}
                pagination={{
                  current: practitionerPage.current,
                  pageSize: practitionerPage.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (current: number, pageSize: number) =>
                    setPractitionerPage({ current, pageSize }),
                }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default withClinic(ServiceBehaviorReport);

